/**
 * Deleted-chat tombstones — stop resurrected sidebar rows after delete.
 *
 * Client removes a chat from jotai, but a later cache/snapshot merge (mate
 * delete, ownership refetch, stale sessionStorage) can paint it again until
 * a second delete + reload. Tombstones filter those ghosts until the server
 * list no longer includes them.
 */

const STORAGE_KEY = 'losi:deleted-chat-tombstones:v1'
const MAX_TOMBSTONES = 100
const TTL_MS = 60 * 60 * 1000

type TombstoneMap = Record<string, number>

function readMap(): TombstoneMap {
  if (typeof window === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as TombstoneMap
    if (!parsed || typeof parsed !== 'object') return {}
    const now = Date.now()
    const next: TombstoneMap = {}
    for (const [id, at] of Object.entries(parsed)) {
      if (typeof at === 'number' && now - at < TTL_MS) next[id] = at
    }
    return next
  } catch {
    return {}
  }
}

function writeMap(map: TombstoneMap) {
  if (typeof window === 'undefined') return
  try {
    const entries = Object.entries(map).slice(-MAX_TOMBSTONES)
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Object.fromEntries(entries))
    )
  } catch {
    // ignore quota
  }
}

export function tombstoneDeletedChat(chatId: string) {
  const id = chatId.trim()
  if (!id) return
  const map = readMap()
  map[id] = Date.now()
  writeMap(map)
}

export function isDeletedChatTombstoned(chatId: string): boolean {
  const id = chatId.trim()
  if (!id) return false
  return Boolean(readMap()[id])
}

export function filterTombstonedChats<T extends { id: string }>(
  chats: T[]
): T[] {
  const map = readMap()
  if (Object.keys(map).length === 0) return chats
  return chats.filter(c => !map[c.id])
}

/** Drop tombstones for ids that are still present on a fresh server list. */
export function pruneTombstonesAgainstServerChats(
  serverChatIds: Iterable<string>
) {
  const map = readMap()
  if (Object.keys(map).length === 0) return
  const stillThere = new Set(
    [...serverChatIds].map(id => id.trim()).filter(Boolean)
  )
  let changed = false
  for (const id of Object.keys(map)) {
    if (!stillThere.has(id)) {
      delete map[id]
      changed = true
    }
  }
  if (changed) writeMap(map)
}
